import { useState, useEffect } from 'react'
import { MessageCircle, X, Send } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '../contexts/LanguageContext'
import { trackWhatsAppClick } from '../utils/analytics'

const LiveSupport = () => {
  const { t } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [isFooterVisible, setIsFooterVisible] = useState(false)

  const whatsappNumber = '905536576454' // 0553 657 64 54 without leading zero and country code format

  const handleSendMessage = () => {
    if (message.trim()) {
      // Track WhatsApp click
      trackWhatsAppClick(window.location.pathname)
      
      const whatsappMessage = encodeURIComponent(message.trim())
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`
      window.open(whatsappUrl, '_blank')
      setMessage('')
      setIsOpen(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSendMessage()
    }
  }

  const [isCartOpen, setIsCartOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const footer = document.querySelector('footer')
      if (footer) {
        const footerTop = footer.getBoundingClientRect().top
        const windowHeight = window.innerHeight
        // Footer görünür olduğunda (ekranın alt kısmına geldiğinde)
        setIsFooterVisible(footerTop <= windowHeight)
      }
    }

    // Check if cart modal is open by observing DOM
    const checkCartModal = () => {
      const cartModal = document.querySelector('[data-cart-modal="open"]')
      setIsCartOpen(cartModal !== null)
    }

    const observer = new MutationObserver(checkCartModal)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-cart-modal'] })

    window.addEventListener('scroll', handleScroll)
    handleScroll() // İlk yüklemede kontrol et
    checkCartModal() // İlk yüklemede kontrol et

    return () => {
      window.removeEventListener('scroll', handleScroll)
      observer.disconnect()
    }
  }, [])

  return (
    <>
      {/* Chat Widget Button */}
      {!isCartOpen && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="fixed bottom-6 right-6 z-40"
        >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`px-6 py-3 rounded-full shadow-lg transition-colors duration-200 font-medium ${
            isFooterVisible
              ? 'bg-white text-black hover:bg-gray-100'
              : 'bg-black text-white hover:bg-gray-800'
          }`}
        >
          {t('liveSupport.title')}
        </button>
        </motion.div>
      )}

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
            />

            {/* Chat Window */}
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl z-40 overflow-hidden"
            >
              {/* Header */}
              <div className="bg-black text-white px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base">{t('liveSupport.title')}</h3>
                    <p className="text-xs text-white/80">{t('liveSupport.responseTime')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Messages */}
              <div className="p-6 space-y-4 max-h-80 overflow-y-auto">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-gray-100 rounded-2xl rounded-tl-none px-4 py-3 max-w-[80%]">
                    <p className="text-sm text-gray-900">
                      {t('liveSupport.welcome')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-gray-100 rounded-2xl rounded-tl-none px-4 py-3 max-w-[80%]">
                    <p className="text-sm text-gray-900">
                      {t('liveSupport.help')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Input Area */}
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={t('liveSupport.placeholderText')}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!message.trim()}
                    className="bg-black text-white px-4 py-2.5 rounded-lg hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
                <button
                  onClick={() => {
                    trackWhatsAppClick(window.location.pathname)
                    const whatsappUrl = `https://wa.me/${whatsappNumber}`
                    window.open(whatsappUrl, '_blank')
                    setIsOpen(false)
                  }}
                  className="w-full bg-black text-white px-4 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>{t('liveSupport.continueWhatsApp')}</span>
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  {t('liveSupport.secure')}
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

export default LiveSupport















